/**
 * Pure-SVG bars: revenue (translucent) and cost (solid) per month, with
 * a margin trend line. No chart lib — keeps the landing bundle tiny.
 */
const DATA = [
  { m: 'Dek', revenue: 62, cost: 48 },
  { m: 'Ýan', revenue: 71, cost: 54 },
  { m: 'Few', revenue: 66, cost: 53 },
  { m: 'Mar', revenue: 84, cost: 61 },
  { m: 'Apr', revenue: 92, cost: 67 },
  { m: 'May', revenue: 108, cost: 75 },
]

export function MarginChart() {
  const W = 540
  const H = 220
  const padX = 28
  const padY = 24
  const innerW = W - padX * 2
  const innerH = H - padY * 2
  const maxVal = 120
  const groupW = innerW / DATA.length
  const barW = 14
  const gap = 6

  const yScale = (v: number) => padY + innerH - (v / maxVal) * innerH

  const marginPts = DATA.map((d, i) => {
    const x = padX + groupW * i + groupW / 2
    const pct = ((d.revenue - d.cost) / d.revenue) * 100
    const y = padY + innerH - (pct / 40) * innerH
    return [x, y] as const
  })

  const linePath = marginPts
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x} ${y}`)
    .join(' ')

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Revenue vs cost by month">
        {/* Gridlines */}
        {[0, 1, 2, 3, 4].map((i) => {
          const y = padY + (innerH / 4) * i
          return (
            <line
              key={i}
              x1={padX}
              x2={W - padX}
              y1={y}
              y2={y}
              stroke="hsl(var(--border))"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
          )
        })}

        {/* Bars */}
        {DATA.map((d, i) => {
          const cx = padX + groupW * i + groupW / 2
          const rx = cx - barW - gap / 2
          const cxBar = cx + gap / 2
          return (
            <g key={d.m}>
              <rect
                x={rx}
                y={yScale(d.revenue)}
                width={barW}
                height={H - padY - yScale(d.revenue)}
                rx={2}
                fill="hsl(var(--primary) / 0.20)"
              />
              <rect
                x={cxBar}
                y={yScale(d.cost)}
                width={barW}
                height={H - padY - yScale(d.cost)}
                rx={2}
                fill="hsl(var(--primary))"
              />
              <text
                x={cx}
                y={H - 6}
                textAnchor="middle"
                fontSize="10"
                fill="hsl(var(--muted-foreground))"
                fontFamily="var(--font-sans), Inter, sans-serif"
              >
                {d.m}
              </text>
            </g>
          )
        })}

        {/* Margin line */}
        <path
          d={linePath}
          fill="none"
          stroke="hsl(var(--positive))"
          strokeWidth={2}
        />
        {marginPts.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={3} fill="hsl(var(--positive))" />
        ))}
      </svg>

      <div className="mt-4 flex items-center gap-4 text-[11px] text-muted-foreground">
        <Legend swatch="bg-primary/30" label="Girdeji" />
        <Legend swatch="bg-primary" label="Çykdajy" />
        <Legend swatch="bg-positive" label="Marja %" round />
      </div>
    </div>
  )
}

function Legend({ swatch, label, round }: { swatch: string; label: string; round?: boolean }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`inline-block w-3 h-3 ${round ? 'rounded-full' : 'rounded-sm'} ${swatch}`} />
      {label}
    </span>
  )
}
